import { validateMilestoneChange, validateSamples, type Sample } from "@anthemreactv2/roast-domain";

export const EVENT_NAMES = ["Turning point", "Color change", "First crack", "Second crack", "Heat adjustment", "Airflow adjustment", "Note", "Drop"] as const;
export type RoastEvent = {id: string; name: typeof EVENT_NAMES[number]; elapsedMs: number; note: string};
export type Curve = {name: string; provenance: "synthetic" | "csv" | "device"; samples: Sample[]};
export type Reference = Curve & {events: RoastEvent[]; profile?: {id: string; revision: number}};
export type EventChange = {id: string; at: number; reason: string; before: RoastEvent | null; after: RoastEvent | null};
export type Workspace = {version: 2; source: Curve; reference: Reference | null; events: RoastEvent[]; history: EventChange[]; playheadMs: number};
export type WorkspaceUpdate = (value: Workspace | ((current: Workspace) => Workspace)) => void;
export const MAX_EVENT_CHANGES = 2000;

/** Deliberately synthetic training fixture. Never substituted for missing device data. */
export function demoWorkspace(): Workspace {
  const knots = [[0, 210], [75, 100], [150, 128], [240, 156], [330, 176], [420, 191], [480, 199], [540, 205], [600, 210]] as const;
  const reference: Sample[] = Array.from({length: 601}, (_, second) => {
    const right = knots.findIndex(knot => knot[0] >= second);
    const a = knots[Math.max(0, right - 1)]!;
    const b = knots[right]!;
    const value = a[0] === b[0] ? a[1] : a[1] + (b[1] - a[1]) * (second - a[0]) / (b[0] - a[0]);
    return {elapsedMs: second * 1000, beanC: value, environmentC: 235 + second / 20};
  });
  return {version: 2, source: {name: "Training roast", provenance: "synthetic", samples: reference.map(s => ({...s, beanC: s.beanC! + 1.8 * Math.sin(s.elapsedMs / 85000)}))},
    reference: {name: "Training reference", provenance: "synthetic", samples: reference, events: [
      {id: "training-turn", name: "Turning point", elapsedMs: 75000, note: "Synthetic reference event"},
      {id: "training-color", name: "Color change", elapsedMs: 240000, note: "Synthetic reference event"},
      {id: "training-crack", name: "First crack", elapsedMs: 480000, note: "Synthetic reference event"},
      {id: "training-drop", name: "Drop", elapsedMs: 600000, note: "Synthetic reference event"},
    ]}, events: [], history: [], playheadMs: 0};
}

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid saved workspace object.");
  return value as Record<string, unknown>;
}
function text(value: unknown, max: number): string {
  if (typeof value !== "string" || value.length > max || !value.trim()) throw new Error("Invalid saved workspace text.");
  return value;
}
function number(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error("Invalid saved workspace number.");
  return value;
}
export function parseCurve(value: unknown): Curve {
  const curve = object(value);
  if (curve.provenance !== "csv" && curve.provenance !== "synthetic" && curve.provenance !== "device") throw new Error("Unknown curve source.");
  if (!Array.isArray(curve.samples)) throw new Error("Saved curve has no samples.");
  const samples = curve.samples.map(value => {
    const sample = object(value);
    return {elapsedMs: number(sample.elapsedMs), beanC: sample.beanC === null ? null : number(sample.beanC),
      environmentC: sample.environmentC === null ? null : number(sample.environmentC)};
  });
  validateSamples(samples);
  return {name: text(curve.name, 160), provenance: curve.provenance, samples};
}
function parseEvents(value: unknown, lastMs: number): RoastEvent[] {
  if (!Array.isArray(value) || value.length > 500) throw new Error("A workspace supports up to 500 annotations.");
  const ids = new Set<string>();
  return value.map(value => {
    const event = object(value);
    const name = text(event.name, 40) as RoastEvent["name"];
    if (!EVENT_NAMES.includes(name)) throw new Error("Unknown event type.");
    const elapsedMs = number(event.elapsedMs);
    if (elapsedMs < 0 || elapsedMs > lastMs) throw new Error("Event is outside the curve.");
    const id = text(event.id, 100);
    if (ids.has(id)) throw new Error("Duplicate saved event identifier.");
    ids.add(id);
    if (typeof event.note !== "string" || event.note.length > 500) throw new Error("Invalid event note.");
    return {id, name, elapsedMs, note: event.note};
  });
}

export function parseReference(value: unknown, legacy = false): Reference {
  const data = object(value);
  const curve = parseCurve(data);
  const events = legacy ? [] : parseEvents(data.events, curve.samples[curve.samples.length - 1]!.elapsedMs);
  if (data.profile === undefined || legacy) return {...curve, events};
  const profile = object(data.profile);
  const revision = number(profile.revision);
  if (!Number.isSafeInteger(revision) || revision < 1) throw new Error("Invalid reference version.");
  return {...curve, events, profile: {id: text(profile.id, 100), revision}};
}

export function parseWorkspace(value: unknown): Workspace {
  const data = object(value);
  if (data.version !== 1 && data.version !== 2) throw new Error("This workspace version is not supported.");
  const source = parseCurve(data.source);
  const reference = data.reference === null ? null : parseReference(data.reference, data.version === 1);
  const playheadMs = number(data.playheadMs);
  const lastMs = source.samples[source.samples.length - 1]!.elapsedMs;
  if (playheadMs < 0 || playheadMs > lastMs) throw new Error("Saved replay position is outside the curve.");
  const events = parseEvents(data.events, lastMs);
  // Older exports retain their annotations, including invalid phase order, for explicit correction.
  const rawHistory = data.version === 1 ? [] : data.history;
  if (!Array.isArray(rawHistory) || rawHistory.length > MAX_EVENT_CHANGES) throw new Error("Invalid or oversized local edit history.");
  const ids = new Set<string>();
  const lastChange = new Map<string, RoastEvent | null>();
  const history: EventChange[] = rawHistory.map(value => {
    const change = object(value);
    const id = text(change.id, 100);
    if (ids.has(id)) throw new Error("Duplicate edit history identifier.");
    ids.add(id);
    const at = number(change.at);
    if (!Number.isFinite(new Date(at).getTime()) || at < 0) throw new Error("Invalid edit history date.");
    const before = change.before === null ? null : parseEvents([change.before], lastMs)[0]!;
    const after = change.after === null ? null : parseEvents([change.after], lastMs)[0]!;
    if ((!before && !after) || (before && after && before.id !== after.id)) throw new Error("Invalid event correction.");
    if (typeof change.reason !== "string" || change.reason.length > 300 || (before && !change.reason.trim())) throw new Error("A correction needs a reason.");
    const eventId = (before ?? after)!.id;
    if (lastChange.has(eventId) && JSON.stringify(lastChange.get(eventId)) !== JSON.stringify(before)) throw new Error("Edit history does not match its previous event.");
    lastChange.set(eventId, after);
    return {id, at, reason: change.reason, before, after};
  });
  for (const [id, event] of lastChange) {
    if (JSON.stringify(events.find(item => item.id === id) ?? null) !== JSON.stringify(event)) throw new Error("Edit history does not match the current annotations.");
  }
  return {version: 2, source, reference, playheadMs, events, history};
}

/** Append a local correction record; imported history is not an authenticated audit log. */
export function changeEvent(workspace: Workspace, input: {id?: string; name: RoastEvent["name"]; elapsedMs: number; note: string} | null, editingId?: string, reason = ""): Workspace {
  const before = editingId ? workspace.events.find(event => event.id === editingId) : null;
  if (editingId && !before) throw new Error("This event no longer exists.");
  if (before && !reason.trim()) throw new Error("Explain why this event is changing.");
  if (reason.length > 300) throw new Error("Keep the correction reason under 300 characters.");
  if (workspace.history.length >= MAX_EVENT_CHANGES) throw new Error("The local edit history is full. Export this workspace before starting another.");
  const after = input ? {...input, id: before?.id ?? crypto.randomUUID(), note: input.note.trim()} : null;
  if (!before && !after) throw new Error("Choose an event to change.");
  const remaining = workspace.events.filter(event => event.id !== editingId);
  if (after) {
    parseEvents([after], workspace.source.samples[workspace.source.samples.length - 1]!.elapsedMs);
    validateMilestoneChange(remaining, after);
    if (remaining.length >= 500) throw new Error("A workspace supports up to 500 annotations.");
  }
  return {...workspace, events: [...remaining, ...(after ? [after] : [])].sort((a, b) => a.elapsedMs - b.elapsedMs),
    history: [...workspace.history, {id: crypto.randomUUID(), at: Date.now(), reason: reason.trim(), before: before ?? null, after}]};
}

export function referenceFromWorkspace(workspace: Workspace, name = workspace.source.name): Reference {
  return structuredClone({...workspace.source, name: text(name.trim(), 160), events: workspace.events});
}

export function eventTimeInput(ms: number) {
  const fraction = Math.floor(ms % 1000);
  return formatTime(ms) + (fraction ? `.${fraction.toString().padStart(3, "0")}` : "");
}
export function parseEventTime(value: string) {
  const match = /^(\d{1,3}):([0-5]\d)(?:\.(\d{1,3}))?$/.exec(value.trim());
  if (!match) throw new Error("Enter a time as mm:ss, optionally with milliseconds, such as 08:30.500.");
  return Number(match[1]) * 60000 + Number(match[2]) * 1000 + Number((match[3] ?? "").padEnd(3, "0"));
}

type SavedWorkspace = {revision: number; workspace: Workspace};
const DB_NAME = "anthem-labs-roast-sandbox";
export function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let blocked = false;
    const request = indexedDB.open(DB_NAME, 2);
    request.onupgradeneeded = () => {
      for (const name of ["workspaces", "library", "libraryIndex"]) {
        if (!request.result.objectStoreNames.contains(name)) request.result.createObjectStore(name);
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      if (blocked) db.close(); else resolve(db);
    };
    request.onerror = () => reject(request.error ?? new Error("Local storage is unavailable."));
    request.onblocked = () => { blocked = true; reject(new Error("Close other Labs tabs to finish opening local storage.")); };
  });
}
export async function loadWorkspace(): Promise<SavedWorkspace | null> {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = db.transaction("workspaces").objectStore("workspaces").get("active");
      request.onsuccess = () => {
        try {
          if (request.result === undefined) return resolve(null);
          const saved = object(request.result);
          const revision = number(saved.revision);
          if (!Number.isSafeInteger(revision) || revision < 1) throw new Error("Invalid local save revision.");
          resolve({revision, workspace: parseWorkspace(saved.workspace)});
        } catch (error) { reject(error); }
      };
      request.onerror = () => reject(request.error);
    });
  } finally { db.close(); }
}

/** Compare-and-swap protects a newer save from another tab. Resolve only on transaction commit. */
export async function saveWorkspace(workspace: Workspace, expectedRevision: number): Promise<number> {
  if (!Number.isSafeInteger(expectedRevision) || expectedRevision < 0) throw new Error("Invalid expected save revision.");
  const validated = parseWorkspace(workspace);
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction("workspaces", "readwrite");
      const store = transaction.objectStore("workspaces");
      const request = store.get("active");
      let error: Error | null = null;
      request.onsuccess = () => {
        const current = request.result === undefined ? 0 : request.result.revision;
        if (current !== expectedRevision) {
          error = new Error("Another Labs tab saved a newer workspace. Export your session, then reload before saving.");
          transaction.abort();
          return;
        }
        store.put({revision: expectedRevision + 1, workspace: validated}, "active");
      };
      transaction.oncomplete = () => resolve(expectedRevision + 1);
      transaction.onabort = () => reject(error ?? transaction.error ?? new Error("Local save was interrupted."));
      transaction.onerror = () => reject(transaction.error ?? new Error("Local save failed."));
    });
  } finally { db.close(); }
}

export function download(contents: string, filename: string, type: string) {
  const url = URL.createObjectURL(new Blob([contents], {type}));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function formatTime(ms: number) {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;
}
